/* eslint-disable @typescript-eslint/no-explicit-any */
import aws4 from 'aws4'
import { Auth } from 'aws-amplify'
import { apis } from '@/config/apiConfig'
import { UserData } from '@/types/user'
import { verifyResult, showMessage } from '@/utilities/util'
import { isUserTeamMember } from '@/utilities/utils'
import { hydrateResponse } from '@/utilities/storage'
import { lookupInBrowserByKey } from '@/utilities/browserLookup'

// Type definitions
interface SignedRequest {
  url: string
  headers: Record<string, string>
  opts: aws4.Request
}

interface ApiResponse {
  success?: boolean
  message?: string
  error?: Error
  data?: unknown
  userUUID?: string
  [key: string]: unknown
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

const mamApis: RegExp[] = [
  /https:\/\/(dv|qa|pd)-fot-tiles.s3.amazonaws.com\//,
  /https:\/\/my-asset-map-data.s3.amazonaws.com\//,
  /https:\/\/.*\.execute-api\.us-east-1\.amazonaws\.com\//, // matches any API Gateway endpoint
]

function apiCheck(url: string): boolean {
  return mamApis.map(mamApi => mamApi.test(url)).includes(true)
}

function signRequest(
  url: string,
  method: HttpMethod = 'GET',
  body: string | null = null,
  contentType: string | null = null
): SignedRequest {
  const urlObject = new URL(url)
  const opts: aws4.Request = {
    host: urlObject.host,
    path: urlObject.pathname + urlObject.search,
  }

  if (body !== null && method !== 'GET') {
    opts.body = body
    opts.method = method
    // An empty content type falls back to JSON too, so `||` is deliberate.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    opts.headers = { 'Content-Type': contentType || 'application/json' }
  }
  if (method === 'DELETE' || method === 'GET') {
    opts.method = method
  }

  if (apiCheck(url)) {
    opts.region = 'us-east-1'
    if (opts.host && /.*.?s3.amazonaws.com/.test(opts.host)) {
      opts.service = 's3'
    } else {
      opts.service = 'execute-api'
    }

    // credentials set in createCredentials function in user.js
    const accessKeyId = localStorage.getItem('accessKeyId')
    const secretAccessKey = localStorage.getItem('secretAccessKey')
    const sessionToken = localStorage.getItem('sessionToken')

    aws4.sign(opts, {
      secretAccessKey: secretAccessKey ?? '',
      accessKeyId: accessKeyId ?? '',
      sessionToken: sessionToken ?? '',
    })
  }
  const rawHeaders = opts.headers ?? {}

  if ('Host' in rawHeaders) {
    delete rawHeaders.Host // consider an unsafe header by browsers
  }
  if ('Content-Length' in rawHeaders) {
    delete rawHeaders['Content-Length'] // consider an unsafe header by browsers
  }

  // Convert to Record<string, string> by filtering out undefined values
  const headers: Record<string, string> = {}
  Object.entries(rawHeaders).forEach(([key, value]) => {
    if (typeof value === 'string') {
      headers[key] = value
    }
  })

  return { url, headers, opts }
}

function addQueryVar(url: string, key: string, value: string): string {
  const encodedKey = encodeURI(key)
  const encodedValue = encodeURI(value)

  const querySplit = url.split('?')
  const urlRoot = querySplit[0]
  let query = ''
  if (querySplit.length === 2) query = querySplit[1]

  const kvp = query.split('&')

  let i = kvp.length
  let x: string[]

  // eslint-disable-next-line no-plusplus
  while (i--) {
    x = kvp[i].split('=')

    // eslint-disable-next-line eqeqeq
    if (x[0] == encodedKey) {
      x[1] = encodedValue
      kvp[i] = x.join('=')
      break
    }
  }

  if (i < 0) {
    kvp[kvp.length] = [encodedKey, encodedValue].join('=')
  }

  return `${urlRoot}?${kvp.join('&')}`.replace('?&', '?')
}

/**
 * Make request to url.
 * @param {string} url - url for request
 * @param {HttpMethod} method - http method type
 * @param {Record<string, any> | null} body - object of key value pairs for post body
 * @param {function} callback - callback function. Optional. Will be called with the response.
 * @returns {Promise<ApiResponse>} - Returns a Promise
 */
const apiFetch = (
  url: string,
  method: HttpMethod = 'GET',
  body: Record<string, unknown> | null = null,
  callback: ((response: ApiResponse) => void) | null = null,
  signal?: AbortSignal
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const contentType = 'application/json'

    const sendFetch = (userUrl: string): Promise<ApiResponse> => {
      let options: RequestInit
      if (method === 'GET') {
        // If GET, append QUERY params to URL instead
        if (body != null && typeof body === 'object') {
          Object.keys(body).forEach(key => {
            userUrl = addQueryVar(userUrl, key, String(body[key]))
          })
        }

        options = {
          method,
          headers: signRequest(userUrl).headers,
          signal,
        }
      } else if (method === 'POST') {
        options = {
          method,
          body: JSON.stringify(body),
          headers: signRequest(
            userUrl,
            method,
            JSON.stringify(body),
            contentType
          ).headers,
          signal,
        }
      } else {
        // For PUT, DELETE, etc.
        options = {
          method,
          body: body ? JSON.stringify(body) : undefined,
          headers: signRequest(
            userUrl,
            method,
            body ? JSON.stringify(body) : null,
            contentType
          ).headers,
          signal,
        }
      }

      return fetch(userUrl, options)
        .then(response => {
          if (response.status >= 200 && response.status <= 299) {
            return Promise.resolve(response.json())
          }
          throw Error(response.statusText)
        })
        .then(async (json: ApiResponse) => {
          const { data } = json
          if (
            typeof data !== 'undefined' &&
            data &&
            typeof data === 'object' &&
            'hydrate' in data
          ) {
            try {
              json.data = await hydrateResponse(
                (data as { hydrate: Parameters<typeof hydrateResponse>[0] })
                  .hydrate
              )
            } catch (err) {
              console.error('Storage error:', err)
            }
          }
          return json
        })
    }

    Auth.currentSession()
      .then(result => {
        const cogID =
          lookupInBrowserByKey('userImpersonation') ??
          (result as any).idToken.payload.sub

        return sendFetch(addQueryVar(url, 'userID', cogID))
          .then(async (json: ApiResponse) => {
            // If user/init or user/tokens, confirm impersonation is allowed (validate via user/init payload).
            if (url.endsWith('/user/init') || url.endsWith('/user/tokens')) {
              if (cogID !== (result as any).idToken.payload.sub) {
                const validateUserJson = await sendFetch(
                  addQueryVar(
                    `${apis.apiDatabase.uri}user/init`,
                    'userID',
                    (result as any).idToken.payload.sub
                  )
                )
                if (
                  !verifyResult(validateUserJson) ||
                  !isUserTeamMember(validateUserJson?.data as UserData)
                ) {
                  throw Error(
                    'You are not allowed to impersonate accounts. Please contact support@myassetmap.com if you need assistance.'
                  )
                }
              }
            }

            // Append userUUID to response
            json.userUUID = cogID
            if (callback) {
              callback(json)
            }
            resolve(json)
          })
          .catch((e: Error) => {
            // Network Error
            console.error(e)
            const errorResponse: ApiResponse = {
              success: false,
              message: e.message,
              error: e,
            }
            if (callback) {
              callback(errorResponse)
            }
            reject(e)
          })
      })
      .catch((e: Error) => {
        // Don't log or show messages for "No current user" - this is expected when not logged in
        const msg = String(e?.message ?? e)
        if (msg.includes('No current user')) {
          console.warn('No current user - expected when not logged in')
        } else {
          console.log('SESSION ERROR', e)
          showMessage('Session Fetch Error', e.message)
        }
        const errorResponse: ApiResponse = {
          success: false,
          message: e.message,
          error: undefined,
        }
        if (callback) {
          callback(errorResponse)
        }
        resolve(errorResponse)
      })
  })
}

export { apiCheck, signRequest, apiFetch, addQueryVar }

export type { ApiResponse, HttpMethod, SignedRequest }
